/**
 * Branding Application Utility for Whitelabel Multi-Tenant Architecture
 *
 * This module applies tenant-specific branding dynamically via CSS variables.
 *
 * CSS Variables Applied:
 * - --primary-color: Main brand color (buttons, links, headers)
 * - --secondary-color: Accent color (highlights, CTAs)
 *
 * Dynamic Updates:
 * - CSS variables applied to :root element
 * - Changes take effect immediately without page reload
 *
 * @module applyBranding
 */

/**
 * Tenant branding configuration interface.
 *
 * Matches backend TenantConfig.BrandingConfig structure.
 */
export interface BrandingConfig {
  /** Primary brand color (hex format, e.g., "#0066CC") */
  primaryColor: string;
  /** Secondary brand color (hex format, e.g., "#FF6600") */
  secondaryColor: string;
}

/**
 * Applies tenant branding dynamically to the application.
 *
 * This function updates CSS variables to match tenant branding:
 * - Sets --primary-color and --secondary-color on :root
 * - Changes take effect immediately without page reload
 *
 * Usage in Tailwind/CSS:
 * ```css
 * // Tailwind config
 * theme: {
 *   extend: {
 *     colors: {
 *       'primary': 'var(--primary-color, #0056B3)',
 *       'secondary': 'var(--secondary-color, #FF6600)'
 *     }
 *   }
 * }
 * ```
 *
 * Example:
 * ```typescript
 * const branding: BrandingConfig = {
 *   primaryColor: '#0066CC',
 *   secondaryColor: '#003D7A'
 * };
 *
 * applyBranding(branding);
 * // CSS variables now available: var(--primary-color) → #0066CC
 * ```
 *
 * @param branding - Tenant branding configuration
 */
export function applyBranding(branding: BrandingConfig): void {
  const root = document.documentElement;

  // Apply CSS variables to :root element
  root.style.setProperty('--primary-color', branding.primaryColor);
  root.style.setProperty('--secondary-color', branding.secondaryColor);

  // Generate color shades from primary color (for tenant-50 to tenant-950)
  generateColorShades(branding.primaryColor);
}

/**
 * Generates color shade variants from primary color.
 * Creates CSS variables --tenant-50 through --tenant-950 for Tailwind classes.
 *
 * @param primaryColor - Base color in hex format
 */
function generateColorShades(primaryColor: string): void {
  const root = document.documentElement;

  // Parse hex color to RGB
  const hex = primaryColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Helper to mix color with white or black
  const mixColor = (baseR: number, baseG: number, baseB: number, mixR: number, mixG: number, mixB: number, ratio: number): string => {
    const r = Math.round(baseR * (1 - ratio) + mixR * ratio);
    const g = Math.round(baseG * (1 - ratio) + mixG * ratio);
    const b = Math.round(baseB * (1 - ratio) + mixB * ratio);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Generate light shades (mix with white)
  root.style.setProperty('--tenant-50', mixColor(r, g, b, 255, 255, 255, 0.9));
  root.style.setProperty('--tenant-100', mixColor(r, g, b, 255, 255, 255, 0.8));
  root.style.setProperty('--tenant-200', mixColor(r, g, b, 255, 255, 255, 0.6));
  root.style.setProperty('--tenant-300', mixColor(r, g, b, 255, 255, 255, 0.4));
  root.style.setProperty('--tenant-400', mixColor(r, g, b, 255, 255, 255, 0.2));

  // Generate dark shades (mix with black)
  root.style.setProperty('--tenant-600', mixColor(r, g, b, 0, 0, 0, 0.2));
  root.style.setProperty('--tenant-700', mixColor(r, g, b, 0, 0, 0, 0.4));
  root.style.setProperty('--tenant-800', mixColor(r, g, b, 0, 0, 0, 0.6));
  root.style.setProperty('--tenant-900', mixColor(r, g, b, 0, 0, 0, 0.8));
  root.style.setProperty('--tenant-950', mixColor(r, g, b, 0, 0, 0, 0.9));
}

/**
 * Removes tenant branding and reverts to defaults.
 *
 * This function removes custom CSS variables and resets to Canassistance defaults.
 * Useful for debugging or switching tenants.
 *
 * @param defaultBranding - Default branding configuration (Canassistance)
 */
export function removeBranding(defaultBranding?: BrandingConfig): void {
  const root = document.documentElement;

  if (defaultBranding) {
    // Apply default branding
    applyBranding(defaultBranding);
  } else {
    // Remove CSS variables entirely
    root.style.removeProperty('--primary-color');
    root.style.removeProperty('--secondary-color');
  }
}

/**
 * Gets currently applied branding from CSS variables.
 *
 * This function reads CSS variables from :root element.
 * Useful for debugging or checking current branding state.
 *
 * @returns BrandingConfig | null - Current branding or null if not applied
 */
export function getCurrentBranding(): Partial<BrandingConfig> | null {
  const root = document.documentElement;
  const style = getComputedStyle(root);

  const primaryColor = style.getPropertyValue('--primary-color').trim();
  const secondaryColor = style.getPropertyValue('--secondary-color').trim();

  if (!primaryColor && !secondaryColor) {
    return null;
  }

  return {
    primaryColor: primaryColor || undefined,
    secondaryColor: secondaryColor || undefined,
  };
}

/**
 * Default branding configuration, applied when no tenant branding is in force.
 *
 * This configuration is used as fallback when:
 * - Origin detection fails (timeout, error)
 * - Origin not recognized (not in whitelist)
 * - Standalone mode (no iframe embedding)
 *
 * Canassistance Branding Guidelines (from Global Plan):
 * - Typography: Inter/Open Sans/Roboto (modern sans-serif)
 * - Colors: White #FFFFFF, neutral #F8F9FA, primary blue #0056B3, text #212529
 * - Imagery: Healthcare/travel warm photography
 * - Style: Professional, clean, mobile-first, WCAG 2.1 AA
 */
export const DEFAULT_BRANDING: BrandingConfig = {
  primaryColor: '#03575c', // Canassistance teal green
  secondaryColor: '#ffb300', // Canassistance yellow/gold
};
